import { __rest } from '../../../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import React__default, { useState, useCallback, useRef, useImperativeHandle, useMemo } from 'react';
import cns from '../../../../node_modules/classnames/index.js';
import { useFloating, useDismiss, useInteractions, FloatingPortal, FloatingFocusManager } from '../../../../node_modules/@floating-ui/react/dist/floating-ui.react.js';
import { NOOP } from '../../../../utils/const.js';
import TextField from '../TextField/TextField.js';
import ExpandMoreIcon from '../../../GoogleIcons/Outlined/ExpandMoreIcon.js';
import ExpandLessIcon from '../../../GoogleIcons/Outlined/ExpandLessIcon.js';
import AddIcon from '../../../GoogleIcons/Outlined/AddIcon.js';
import Button from '../../Button/Button.js';
import List from './List.js';
import useCurrentListItem from './useCurrentListItem.js';
import Spinner from '../../../Spinner/Spinner.js';
import { getInputState, SelectStatesEnum } from '../common.js';
import { listPlacement } from './common.js';
import useSearchValueChange from './useSearchValueChange.js';
import { createFocus, createContains } from '../shared.js';
import { autoUpdate } from '../../../../node_modules/@floating-ui/dom/dist/floating-ui.dom.js';
import { offset, flip, size } from '../../../../node_modules/@floating-ui/react-dom/dist/floating-ui.react-dom.js';

const SelectField = React__default.forwardRef((_a, ref) => {
    var { className, listClassName, disabled, onKeyDown, readOnly, state, searchValue, items, onSelected = NOOP, onSearchValueChange, renderItem, addItemLabel, onAddItem, onUpdateItem, onRemoveItem, listRight, listTop, onShowLastItem, itemKeyProperty, onSelectBlur = NOOP } = _a, textFieldProps = __rest(_a, ["className", "listClassName", "disabled", "onKeyDown", "readOnly", "state", "searchValue", "items", "onSelected", "onSearchValueChange", "renderItem", "addItemLabel", "onAddItem", "onUpdateItem", "onRemoveItem", "listRight", "listTop", "onShowLastItem", "itemKeyProperty", "onSelectBlur"]);
    const [showPopup, setShowPopup] = useState(false);
    const [currentListItem, selectItem, handleKeyDown] = useCurrentListItem(items, onSelected, setShowPopup, onKeyDown);
    const handleItemSelected = useCallback((value, event) => {
        selectItem(value, event);
        setShowPopup(false);
    }, [selectItem, setShowPopup]);
    const handleInputChange = useSearchValueChange(onSearchValueChange || NOOP, setShowPopup);
    const renderListItem = useCallback((item) => renderItem(item, searchValue || ''), [renderItem, searchValue]);
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => ({
        selectItem: handleItemSelected,
        hidePopup: () => setShowPopup(false),
        focus: createFocus(inputRef),
        contains: createContains(inputRef),
        getSearchValue: () => (inputRef.current ? inputRef.current.value : undefined),
    }), [handleItemSelected, setShowPopup]);
    const textfieldState = getInputState(state);
    const placement = useMemo(() => listPlacement(listTop, listRight), [listTop, listRight]);
    const { refs, floatingStyles, context, elements: { floating }, } = useFloating({
        placement,
        open: showPopup,
        onOpenChange: setShowPopup,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(2),
            flip(),
            size({
                apply({ rects, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                        maxHeight: `${availableHeight}px`,
                    });
                },
                padding: 10,
            }),
        ],
    });
    const dismiss = useDismiss(context);
    useInteractions([dismiss]);
    const onBlur = useCallback((event) => {
        if (event.relatedTarget !== event.currentTarget
            && !event.currentTarget.contains(event.relatedTarget)
            && !(floating === null || floating === void 0 ? void 0 : floating.contains(event.relatedTarget))) {
            onSelectBlur(event);
        }
    }, [onSelectBlur, floating]);
    return (jsxs("div", { ref: refs.setReference, onBlur: onBlur, onKeyDown: handleKeyDown, className: cns('v2-select-field', className), children: [jsx(TextField, Object.assign({ ref: inputRef }, textFieldProps, { value: searchValue || '', action: showPopup ? jsx(ExpandLessIcon, {}) : jsx(ExpandMoreIcon, {}), onActionClick: () => setShowPopup((visible) => !visible), onChange: handleInputChange, className: "v2-select-field__input", disabled: disabled, onKeyDown: handleKeyDown, readOnly: readOnly, state: textfieldState })), showPopup && !disabled && !readOnly && (jsx(FloatingPortal, { children: jsx(FloatingFocusManager, { context: context, modal: false, initialFocus: inputRef, children: jsx("div", { ref: refs.setFloating, style: floatingStyles, onKeyDown: handleKeyDown, className: "v2-select-field floating-ui", children: jsx("div", { className: "v2-form-field-popup v2-select-field__options", children: state !== SelectStatesEnum.loading
                                ? (jsxs(Fragment, { children: [jsx(List, { className: listClassName, current: currentListItem, items: items, onUpdateItem: onUpdateItem, onRemoveItem: onRemoveItem, onSelected: handleItemSelected, renderItem: renderListItem, onShowLastItem: onShowLastItem, itemKeyProperty: itemKeyProperty }), onAddItem ? (jsx("div", { className: "v2-select-field__add-item", children: jsxs(Button, { onClick: onAddItem, children: [jsx(AddIcon, {}), addItemLabel] }) })) : null] }))
                                : jsx(Spinner, { small: true }) }) }) }) }))] }));
});

export { SelectField as default };
